import { FC, useMemo } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import Button from "@/components/Button"
import OutlineButton from "@/components/OutlineButton"
import {
  type AccessBlockedDetails,
  accessBlockedDetailsFromData,
  inboxingBillingUrlFromData,
} from "@/core/api/accessBlocked"
import { safeExternalUrl } from "@/utils/safeExternalUrl"

const detailsFromSearch = (search: string, state: unknown) => {
  if (
    state &&
    typeof state === "object" &&
    "accessBlocked" in state &&
    state.accessBlocked
  ) {
    return state.accessBlocked as AccessBlockedDetails
  }

  const params = new URLSearchParams(search)
  const code = params.get("code")
  const title = params.get("title")
  const message = params.get("message")
  const billingUrl = params.get("billingUrl") || undefined
  const status = params.get("status") || undefined
  const returnTo = params.get("returnTo") || undefined

  return (
    accessBlockedDetailsFromData(
      {
        code,
        message,
        billingUrl,
      },
      status,
    ) || {
      code: code || "BILLING_REQUIRED",
      title: title || "Access blocked",
      message:
        message ||
        "Your account does not currently have access to this DreamSender capability.",
      billingUrl,
      status,
      returnTo,
    }
  )
}

const AccessBlockedPage: FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const details = useMemo(
    () => detailsFromSearch(location.search, location.state),
    [location.search, location.state],
  )
  const returnTo =
    details.returnTo || new URLSearchParams(location.search).get("returnTo")
  const billingUrl = safeExternalUrl(
    inboxingBillingUrlFromData(details, { fallback: true }),
  )

  return (
    <div className="flex h-full min-h-[420px] items-center justify-center">
      <div
        className="max-w-2xl rounded-md border border-warning bg-white p-6 shadow-sm"
        data-testid="access-blocked"
      >
        <div className="text-sm font-medium uppercase tracking-wide text-warning">
          {details.status ? `Error ${details.status}` : "Access blocked"}
        </div>
        <h1 className="mt-2 text-2xl font-semibold">{details.title}</h1>
        <p className="mt-3 text-sm leading-6 text-text-light">
          {details.message}
        </p>
        <div className="mt-4 rounded-md bg-warning/10 px-4 py-3 text-sm">
          Read-only pages remain available when your subscription allows them.
          Billing-limited actions will stay blocked until the entitlement is
          restored.
        </div>
        <div className="mt-5 text-xs text-text-light">Code: {details.code}</div>
        <div className="mt-6 flex flex-wrap gap-3">
          {billingUrl && (
            <a
              className="btn btn-primary btn-sm min-w-[44px] rounded-md border-none bg-primary px-4 py-3 text-white normal-case"
              href={billingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="inboxing-billing-link"
            >
              Open Inboxing billing
            </a>
          )}
          {returnTo && (
            <Button className="btn-sm" onClick={() => navigate(returnTo)}>
              Return to previous page
            </Button>
          )}
          <OutlineButton
            className="btn-sm bg-white text-black"
            onClick={() => navigate("/organizations")}
          >
            Back to organizations
          </OutlineButton>
        </div>
      </div>
    </div>
  )
}

export default AccessBlockedPage
